/*eslint-disable */
import {
  CoworkerRequired,
  getAgreementsByCustomerId,
  transferActiveClub,
  UpdateActiveClub,
} from '../../api/user';

export interface ClubContext {
  readonly coRenterCustomerId?: string;
  readonly coRenterFirstName?: string;
  readonly coRenterLastName?: string;
  readonly coRenterClubId?: string;
  readonly coRenterClubStoreNumber?: string;
  readonly coRenterClubActive?: boolean;
  readonly coRenterClubState?: string;

  readonly primaryCustomerId?: string;
  readonly primaryFirstName?: string;
  readonly primaryLastName?: string;
  readonly primaryClubActive?: boolean;
  readonly primaryClubState?: string;
  readonly primaryClubId?: string;
  readonly primaryClubStoreNumber?: string;
}

interface CustomerInfo {
  status: boolean;
  context?: ClubContext;
  data?: any;
}

export const processTransferOfClub = async (
  customerIds: string[]
): Promise<any> => {
  for (let idx = 0; idx < customerIds.length; idx++) {
    await processOneCustomer(customerIds[idx]);
  }
};

export const processOneCustomer = async (customerId: string): Promise<any> => {
  const context = await buildContext(customerId);

  const toStore = window.sessionStorage.getItem('storeNumber'); // the current store in the RECEIVE must be the receive store !

  if (toStore) {
    // now lets transfer or cancel the primary club
    if (
      context?.primaryClubActive &&
      context.primaryCustomerId &&
      context.primaryClubStoreNumber
    ) {
      if (await storesInSameProgram(toStore, context.primaryClubStoreNumber)) {
        await transferClub(
          context.primaryCustomerId,
          context.primaryClubStoreNumber,
          toStore
        );
      } else {
        await cancelClub(context?.primaryCustomerId);
      }
    }

    if (
      context?.coRenterClubActive &&
      context.coRenterCustomerId &&
      context.coRenterClubStoreNumber
    ) {
      if (await storesInSameProgram(toStore, context.coRenterClubStoreNumber)) {
        await transferClub(
          context.coRenterCustomerId,
          context.coRenterClubStoreNumber,
          toStore
        );
      } else {
        await cancelClub(context?.coRenterCustomerId);
      }
    }
  }
};

export const storesInSameProgram = async (
  toStore,
  fromStore
): Promise<boolean> => {
  const allstores: string[] = [toStore, fromStore].filter(Boolean);
  const payload = {
    storeNumbers: allstores,
    paramKeyNames: ['BenefitPlusClubId'],
  };

  const out = await CoworkerRequired(payload);
  if (out.status == 200) {
    let toStoreProgram;
    let fromStoreProgram;

    for (const oneConfig of out.data?.storeProfileResponse?.configDetails) {
      const programId = oneConfig?.configDetails[0]?.paramValue;

      if (oneConfig?.storeNumber == toStore) {
        toStoreProgram = programId;
      }

      if (oneConfig?.storeNumber == fromStore) {
        fromStoreProgram = programId;
      }
    }

    if (toStoreProgram == fromStoreProgram) {
      return true;
    }
  }

  return false;
};

export const cancelClub = async (customerId: string): Promise<any> => {
  const payload = {
    isCreate: 0,
    customerId: Number(customerId),
    clubStatusRefCode: 'C',
  };

  return await UpdateActiveClub(payload);
};

export const setPrimary = (context: ClubContext, data: any): ClubContext => {
  if (data?.clubInfoResponse) {
    const activeClub = data.clubInfoResponse.find(
      (best) => best.agreementSubstatus == 'Active'
    );

    if (activeClub) {
      context = {
        ...context,
        primaryCustomerId: data.customerId,
        primaryFirstName: data.customerFirstName,
        primaryLastName: data.customerLastName,
        primaryClubActive: true,
        primaryClubState: activeClub.stateProvinceAbbreviation,
        primaryClubId: activeClub.agreementId,
        primaryClubStoreNumber: activeClub.storeNumber,
      };
    } else {
      context = {
        ...context,
        primaryCustomerId: data.customerId,
        primaryFirstName: data.customerFirstName,
        primaryLastName: data.customerLastName,
        primaryClubActive: false,
        primaryClubState: '',
        primaryClubId: '',
        primaryClubStoreNumber: '',
      };
    }
  }
  return context;
};

export const setCoRenter = (context: ClubContext, data: any): ClubContext => {
  if (data?.clubInfoResponse) {
    const activeClub = data.clubInfoResponse.find(
      (best) => best.agreementSubstatus == 'Active'
    );

    if (activeClub) {
      context = {
        ...context,
        coRenterCustomerId: data.coCustomerId,
        coRenterFirstName: data.coCustomerFirstName,
        coRenterLastName: data.coCustomerLastName,
        coRenterClubActive: true,
        coRenterClubState: activeClub.stateProvinceAbbreviation,
        coRenterClubId: activeClub.agreementId,
        coRenterClubStoreNumber: activeClub.storeNumber,
      };
    } else {
      context = {
        ...context,
        coRenterCustomerId: data.coCustomerId,
        coRenterFirstName: data.coCustomerFirstName,
        coRenterLastName: data.coCustomerLastName,
        coRenterClubActive: false,
        coRenterClubState: '',
        coRenterClubId: '',
        coRenterClubStoreNumber: '',
      };
    }
  }
  return context;
};

export const getCustomerInfo = async (
  customerId: string
): Promise<CustomerInfo> => {
  const customerAgreementsAndClub: any = await getAgreementsByCustomerId(
    customerId
  );

  if (customerAgreementsAndClub.status == 200) {
    let context: ClubContext = {};
    if (customerId === customerAgreementsAndClub?.data?.customerId) {
      const out = setPrimary(context, customerAgreementsAndClub.data);
      context = { ...context, ...out };
    }
    // note:  coCustomerId  this is the COrenter
    if (customerId === customerAgreementsAndClub?.data?.coCustomerId) {
      const out = setCoRenter(context, customerAgreementsAndClub.data);
      context = { ...context, ...out };
    }

    return {
      context: context,
      status: true,
      data: customerAgreementsAndClub.data,
    };
  }

  return { status: false };
};

export const buildContext = async (
  customerId: string
): Promise<ClubContext> => {
  let finalContext: ClubContext = {};

  const { status, context, data }: CustomerInfo = await getCustomerInfo(
    customerId
  );

  if (status) {
    finalContext = { ...context };

    let customers = data?.agreement?.find(
      (oneAgm) => {
        const custlist = oneAgm?.customers;
        if ( custlist && custlist.length > 1 ) {
          return oneAgm;
        }
      }
    );

    if( customers ) {
      customers = customers.customers;
      for (const oneCust of customers) {
        if (oneCust.customerId && oneCust.customerId !== customerId) {
          const otherCustomerId = oneCust.customerId;
          const { status, context } = await getCustomerInfo(otherCustomerId);
          if (status) {
            finalContext = { ...finalContext, ...context };
          }
        }
      }
    }
  }

  return finalContext;
};

export const transferClub = async (
  customerId: string,
  fromStore: string,
  toStore: string
): Promise<boolean> => {
  const payload = {
    fromStore: fromStore,
    customerId: Number(customerId),
    transferToStore: toStore,
  };

  const out = await transferActiveClub(payload);

  if (out?.status != 200) {
    return false;
  }

  return true;
};
